import { Component, Input, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../../shared/material.module';
import { RouterLink } from '@angular/router';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { LargeShareholderHoldingsResponseDto, LargeShareholderHoldingDto } from '../../../models/api/largeShareholderHoldingsDto';

interface GroupOption {
  key: string | number;
  label: string;
  count: number;
}

@Component({
  selector: 'app-large-shareholder-holdings-paginated',
  standalone: true,
  imports: [CommonModule, MaterialModule, RouterLink],
  templateUrl: './large-shareholder-holdings-paginated.component.html',
  styleUrls: ['./large-shareholder-holdings-paginated.component.scss']
})
export class LargeShareholderHoldingsPaginatedComponent {
  @Input() set holdings(value: LargeShareholderHoldingsResponseDto | null) {
    this._holdings = value;
    this.buildGroupOptions();
    this.updateHoldingsDataSource();
  }

  @ViewChild(MatSort) set sort(value: MatSort) {
    if (value) {
      this.holdingsDataSource.sort = value;
    }
  }
  @ViewChild(MatPaginator) set paginator(value: MatPaginator) {
    if (value) {
      this.holdingsDataSource.paginator = value;
    }
  }

  _holdings: LargeShareholderHoldingsResponseDto | null = null;
  holdingsViewMode: 'year' | 'company' = 'year';
  holdingsDataSource = new MatTableDataSource<LargeShareholderHoldingDto>([]);
  holdingsFilter = '';

  yearOptions: GroupOption[] = [];
  companyOptions: GroupOption[] = [];
  selectedYear: number | null = null;
  selectedCompanyId: string | null = null;

  holdingsByYearDisplayedColumns: string[] = [
    'companyName', 'firstClassSharesPercentage', 'firstClassShares',
    'secondClassSharesPercentage', 'secondClassShares',
    'thirdClassSharesPercentage', 'thirdClassShares'
  ];

  holdingsByCompanyDisplayedColumns: string[] = [
    'year', 'firstClassSharesPercentage', 'firstClassShares',
    'secondClassSharesPercentage', 'secondClassShares',
    'thirdClassSharesPercentage', 'thirdClassShares'
  ];

  constructor() {
    this.holdingsDataSource.sortingDataAccessor = (item: LargeShareholderHoldingDto, property: string) => {
      switch (property) {
        case 'companyName': return item.companyName || '';
        case 'year': return item.year;
        case 'firstClassSharesPercentage': return item.firstClassSharesPercentage ?? -1;
        case 'firstClassShares': return item.firstClassShares ?? -1;
        case 'secondClassSharesPercentage': return item.secondClassSharesPercentage ?? -1;
        case 'secondClassShares': return item.secondClassShares ?? -1;
        case 'thirdClassSharesPercentage': return item.thirdClassSharesPercentage ?? -1;
        case 'thirdClassShares': return item.thirdClassShares ?? -1;
        default: return (item as any)[property];
      }
    };
  }

  private buildGroupOptions() {
    const holdings = this._holdings?.holdings || [];

    // Build year options
    const yearMap = new Map<number, number>();
    for (const h of holdings) {
      yearMap.set(h.year, (yearMap.get(h.year) || 0) + 1);
    }
    this.yearOptions = Array.from(yearMap.entries())
      .sort(([a], [b]) => b - a)
      .map(([year, count]) => ({ key: year, label: String(year), count }));

    // Build company options
    const companyMap = new Map<string, { name: string; count: number }>();
    for (const h of holdings) {
      const existing = companyMap.get(h.companyId);
      if (existing) {
        existing.count++;
      } else {
        companyMap.set(h.companyId, { name: h.companyName, count: 1 });
      }
    }
    this.companyOptions = Array.from(companyMap.entries())
      .sort(([, a], [, b]) => a.name.localeCompare(b.name))
      .map(([id, data]) => ({ key: id, label: data.name, count: data.count }));

    // Auto-select first option if not already selected
    if (this.yearOptions.length > 0 && this.selectedYear === null) {
      this.selectedYear = this.yearOptions[0].key as number;
    }
    if (this.companyOptions.length > 0 && this.selectedCompanyId === null) {
      this.selectedCompanyId = this.companyOptions[0].key as string;
    }
  }

  onHoldingsViewModeChange(mode: 'year' | 'company') {
    this.holdingsViewMode = mode;
    this.holdingsFilter = '';
    this.holdingsDataSource.filter = '';
    this.updateHoldingsDataSource();
  }

  onHoldingsGroupChange() {
    this.holdingsFilter = '';
    this.holdingsDataSource.filter = '';
    this.updateHoldingsDataSource();
  }

  updateHoldingsDataSource() {
    const holdings = this._holdings?.holdings || [];
    let filtered: LargeShareholderHoldingDto[];

    if (this.holdingsViewMode === 'year') {
      filtered = holdings.filter(h => h.year === this.selectedYear);
      filtered.sort((a, b) => (a.companyName || '').localeCompare(b.companyName || ''));
    } else {
      filtered = holdings.filter(h => h.companyId === this.selectedCompanyId);
      filtered.sort((a, b) => a.year - b.year);
    }

    this.holdingsDataSource.data = filtered;

    // Set up custom filter
    this.holdingsDataSource.filterPredicate = (data: LargeShareholderHoldingDto, filter: string) => {
      const search = filter.toLowerCase();
      if (this.holdingsViewMode === 'year') {
        return (data.companyName || '').toLowerCase().includes(search);
      } else {
        return String(data.year).includes(search);
      }
    };

    if (this.paginator) {
      this.paginator.firstPage();
    }
  }

  applyHoldingsFilter(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.holdingsFilter = value;
    this.holdingsDataSource.filter = value.trim().toLowerCase();
    if (this.paginator) {
      this.paginator.firstPage();
    }
  }

  formatPercentage(value?: number | null): string {
    if (value == null) return '';
    return value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';
  }

  formatNumber(value?: number | null): string {
    if (value == null) return '';
    return value.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }
}
